import { forwardRef, type ReactElement, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import * as styles from './DataTableSettingsModal.sty.js';
import { Button } from '../../../../buttons/button/Button.js';
import { useId } from '../../../../core/hooks/useId.js';
import { Flex } from '../../../../layouts/flex/Flex.js';
import { Modal, type ModalProps } from '../../../../overlays/modal/Modal.js';
import { screenReaderOnly } from '../../../../styles/screen-reader-only.sty.js';
import { dataTableMessages } from '../../messages.js';

/**
 * @internal
 */
export interface DataTableSettingsModalProps
  extends Omit<ModalProps, 'footer' | 'size'> {
  /** The callback that is called for resetting changes. */
  onReset?: () => void;
  /** The callback that is called for applying changes  */
  onApply?: () => void;
  /** The title of the modal. */
  title: string | ReactElement;
  /** Labels for the reset, cancel and apply buttons. */
  controlLabels: {
    reset?: string;
    cancel?: string;
    apply?: string;
  };
  /** Determines whether the reset and/or the apply buttons are disabled. */
  disableButtons?: { reset?: boolean; apply?: boolean };
  /** Whether a status message for the screen reader should be displayed on successful reset. */
  showResetStatus?: boolean;
  /** Optional footerMessage rendered above the footer action buttons (e.g. a warning or error). */
  footerMessage?: ReactNode;
}

/**
 * Modal that allows to apply, reset or cancel changes on the given settings.
 * @internal
 */
export const DataTableSettingsModal = forwardRef<
  HTMLDivElement,
  DataTableSettingsModalProps
>((props, forwardedRef): ReactElement | null => {
  const {
    title,
    dismissible = true,
    show,
    showResetStatus = false,
    children,
    onDismiss,
    onReset,
    onApply,
    controlLabels,
    disableButtons,
    footerMessage,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    className: consumerClassName,
    style: consumerStyle,
  } = props;

  const intl = useIntl();
  const statusId = useId();

  const showFooter = onDismiss || onReset || onApply;

  return (
    <Modal
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      ref={forwardedRef}
      className={consumerClassName}
      style={consumerStyle}
      title={title}
      size="small"
      show={show}
      dismissible={dismissible}
      onDismiss={onDismiss}
      footer={
        showFooter && (
          <Flex
            flexDirection="column"
            gap={12}
            width="100%"
            className={styles.footer[footerMessage ? 'withMessage' : 'default']}
          >
            {footerMessage}
            <Flex justifyContent="space-between" width="100%">
              {onReset && (
                <>
                  <Button
                    onClick={onReset}
                    variant="emphasized"
                    disabled={disableButtons?.reset === true}
                  >
                    {controlLabels?.reset}
                  </Button>
                  <div
                    id={statusId}
                    role="status"
                    aria-live="polite"
                    aria-relevant="all"
                    className={screenReaderOnly}
                  >
                    {showResetStatus &&
                      intl.formatMessage(dataTableMessages.resetSuccessful)}
                  </div>
                </>
              )}

              <Flex justifyContent="flex-end" gap={8}>
                {onDismiss && (
                  <Button onClick={onDismiss} variant="default">
                    {controlLabels?.cancel}
                  </Button>
                )}
                {onApply && (
                  <Button
                    onClick={onApply}
                    variant="accent"
                    color="primary"
                    disabled={disableButtons?.apply === true}
                  >
                    {controlLabels?.apply}
                  </Button>
                )}
              </Flex>
            </Flex>
          </Flex>
        )
      }
    >
      {children}
    </Modal>
  );
});
